import React, { useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, Divider, Stack, TextField, MenuItem,
  Button, InputAdornment, IconButton, RadioGroup, FormControlLabel, Radio,
  FormLabel, FormControl, Snackbar, Alert, CircularProgress, Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { getBearerToken, postBearerToken, putBearerToken } from '../../utils/Apis';
import { useLocation, useNavigate } from 'react-router-dom';
import AppShell from '../../layouts/AppShell';
import { Constants } from '../../utils/Constants';
import './cssClasses/custom.css';

// Create / edit a Host or Participant under this Admin.
// NOTE: Zoom credentials are NOT entered here anymore. Hosts inherit the
// Admin's Connect-Zoom connection automatically, so this form is just the
// person's login + role + (optional) room assignment.
const A_add_admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = location.pathname === '/edit_sub_admin';
  // Host (SubAdmin) login: can only add/edit participants, in the room(s) they run.
  const isHost = (localStorage.getItem(Constants.USERTYPE) || '') === 'SubAdmin';

  // 'User' = Participant (joins rooms), 'SubAdmin' = Host (runs a room)
  const [type, setType] = useState('User');
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [slugName, setSlugName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [room, setRoom] = useState('');
  const [isActive, setIsActive] = useState('Active');
  const [showPassword, setShowPassword] = useState(false);

  const [rooms, setRooms] = useState([]);
  const [loader, setLoader] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [toast, setToast] = useState(null); // {severity, msg}
  const notify = (msg, severity = 'info') => setToast({ msg, severity });

  React.useEffect(() => {
    fetchRooms();
    if (location?.state?.id) fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRooms = async () => {
    const r = await getBearerToken('room');
    if (r && r.error === 0) {
      const list = r.data || [];
      setRooms(list);
      // A host with one room: pre-select it.
      if (isHost && list.length === 1 && !location?.state?.id) setRoom(list[0]._id);
    }
  };

  const fetchDetails = async () => {
    setFetchLoader(true);
    const r = await getBearerToken('users?_id=' + location.state.id);
    if (r && r.error === 0) {
      const d = r.data;
      setType(d.type === 'SubAdmin' ? 'SubAdmin' : 'User');
      setFirstName(d.first_name || '');
      setPhone(d.phone || '');
      setEmail(d.email || '');
      setSlugName(d.slug_name || '');
      setRoom((d.room && (d.room._id || d.room)) || '');
      setIsActive(d.is_active ? 'Active' : 'Inactive');
    }
    setFetchLoader(false);
  };

  // Validation
  const nameError = submitted && firstName.trim() === '';
  const phoneError = submitted && phone.trim() === '';
  const userError = submitted && slugName.trim() === '';
  const passError = submitted && !isEdit && password === '';
  const confirmError = submitted && password !== confirmPassword;

  const submit = async () => {
    setSubmitted(true);
    if (firstName.trim() === '' || slugName.trim() === '' || phone.trim() === '') {
      notify('Full Name, Username and Mobile are required.', 'error');
      return;
    }
    if (!isEdit && password === '') { notify('Password is required.', 'error'); return; }
    if (password !== confirmPassword) { notify('Passwords do not match.', 'error'); return; }
    if (isHost && room === '') { notify('Please choose your room.', 'error'); return; }

    const base = {
      first_name: firstName,
      phone,
      email,
      slug_name: slugName,
      is_active: isActive === 'Active',
      type: isHost ? 'User' : type, // 'User' or 'SubAdmin' (hosts only add participants)
    };
    if (room !== '') base.room = room;

    setLoader(true);
    if (!isEdit) {
      const json = { ...base, password, show_password: password };
      const r = await postBearerToken('users', json);
      setLoader(false);
      if (r && r.error === 0) {
        notify(type === 'SubAdmin' ? 'Host created successfully.' : 'Participant created successfully.', 'success');
        setFirstName(''); setPhone(''); setEmail(''); setSlugName('');
        setPassword(''); setConfirmPassword(''); setSubmitted(false);
        setRoom(isHost && rooms.length === 1 ? rooms[0]._id : ''); // keep a host's only room selected
      } else {
        notify((r && r.status) || 'Something went wrong.', 'error');
      }
    } else {
      const json = { ...base, _id: location.state.id };
      if (password !== '') { json.password = password; json.show_password = password; }
      const r = await putBearerToken('users?_id=' + location.state.id, json);
      setLoader(false);
      if (r && r.error === 0) notify('Saved successfully.', 'success');
      else notify((r && r.status) || 'Something went wrong.', 'error');
    }
  };

  return (
    <AppShell title={isHost ? (isEdit ? 'Edit Participant' : 'Add Participant') : (isEdit ? 'Edit User' : 'Add User')}
      breadcrumb={[isHost ? 'Participants' : 'Users', isEdit ? 'Edit' : 'Add']}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} color="inherit" onClick={() => navigate('/sub_admin_list')} sx={{ color: 'text.secondary' }}>
          {isHost ? 'Back to participants' : 'Back to users'}
        </Button>
      </Stack>

      <Box sx={{ maxWidth: 720 }}>
        <Card>
          <CardHeader
            title={isHost ? (isEdit ? 'Edit participant' : 'Add a participant to your room') : (isEdit ? 'Edit user' : 'Add a host or participant')}
            subheader={isHost
              ? 'They log in on the phone or desktop app with this username and password, and join your room.'
              : 'Hosts run meeting rooms; participants join them. No Zoom setup needed — hosts use your connected Zoom account.'}
            titleTypographyProps={{ variant: 'h5' }}
          />
          <Divider />
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            {fetchLoader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <Stack spacing={3}>
                {!isHost && (
                  <TextField
                    select label="Type" value={type} onChange={(e) => setType(e.target.value)}
                    helperText="Host = runs a room · Participant = joins a room"
                  >
                    <MenuItem value="User">Participant</MenuItem>
                    <MenuItem value="SubAdmin">Host</MenuItem>
                  </TextField>
                )}

                <TextField label="Full Name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  error={nameError} helperText={nameError ? 'Full name is required' : ' '} />

                <TextField label="Mobile Number" value={phone} onChange={(e) => setPhone(e.target.value)}
                  error={phoneError} helperText={phoneError ? 'Mobile number is required' : ' '} />

                <TextField label="Username" value={slugName} inputProps={{ maxLength: 20, autoComplete: 'off', name: 'new-user-name' }}
                  onChange={(e) => setSlugName(e.target.value)}
                  error={userError} helperText={userError ? 'Username is required' : 'Used to log in'} />

                <TextField
                  label="Password" type={showPassword ? 'text' : 'password'}
                  inputProps={{ autoComplete: 'new-password', name: 'new-user-password' }}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  error={passError}
                  helperText={passError ? 'Password is required' : (isEdit ? 'Leave blank to keep the current password' : ' ')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm Password" type={showPassword ? 'text' : 'password'}
                  inputProps={{ autoComplete: 'new-password' }}
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmError} helperText={confirmError ? 'Passwords do not match' : ' '}
                />

                <TextField label="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} helperText=" " />

                <TextField
                  select label={isHost ? 'Room' : 'Assign to Room (optional)'} value={room}
                  onChange={(e) => setRoom(e.target.value)}
                  error={isHost && submitted && room === ''}
                  helperText={isHost ? 'One of the rooms you host' : 'Which room this person belongs to'}
                >
                  {!isHost && <MenuItem value=""><em>None</em></MenuItem>}
                  {rooms.map((item) => (
                    <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
                  ))}
                </TextField>

                <FormControl>
                  <FormLabel sx={{ mb: 0.5, fontWeight: 600 }}>Status</FormLabel>
                  <RadioGroup row value={isActive} onChange={(e) => setIsActive(e.target.value)}>
                    <FormControlLabel value="Active" control={<Radio />} label="Active" />
                    <FormControlLabel value="Inactive" control={<Radio />} label="Inactive" />
                  </RadioGroup>
                </FormControl>

                <Divider />
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button color="inherit" onClick={() => navigate('/sub_admin_list')} sx={{ color: 'text.secondary' }}>Cancel</Button>
                  <Button variant="contained" onClick={submit} disabled={loader}
                    startIcon={loader ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}>
                    {loader ? 'Saving…' : (isEdit ? 'Save changes' : 'Create')}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </AppShell>
  );
};

export default A_add_admin;
